import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getOptionSettings } from '../../lib/api/booking' // ensure tree-shake friendliness; not used directly
import { useBooking } from '../../store/bookingStore'
import { useAuth } from '../../store/authStore'
import { useCalculatedPrice } from '../../lib/useCalculatedPrice'
import { StepShell } from '../../components/layout/StepShell'
import { Field, Checkbox } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import { COUNTRIES } from '../../lib/countries'
import { stepPath } from '../../lib/steps'
import type { BookingInfo } from '../../lib/api/types'

void getOptionSettings

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }))

export function InfoStep() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { locale: routeLocale = 'it' } = useParams()

  const cart = useBooking((s) => s.cart)
  const info = useBooking((s) => s.bookingInfo)
  const setBookingInfo = useBooking((s) => s.setBookingInfo)
  const user = useAuth((s) => s.user)
  useCalculatedPrice()

  useEffect(() => {
    if (!cart.transportId || !cart.fromDay || !cart.fromHour || !cart.toDay)
      navigate(stepPath(routeLocale, 'dates'), { replace: true })
  }, [cart.transportId, cart.fromDay, cart.fromHour, cart.toDay, navigate, routeLocale])

  // Logged in → prefill from the member profile (only empty fields, so typed values win).
  useEffect(() => {
    if (!user) return
    const cur = useBooking.getState().bookingInfo
    const looksLikeEmail = (s: string) => /@/.test(s)
    const patch: Partial<BookingInfo> = {}
    // some accounts store the email as the name (password sign-up) — skip those
    if (!cur.name && user.name && !looksLikeEmail(user.name)) patch.name = user.name
    if (!cur.lastname && user.lastname) patch.lastname = user.lastname
    if (!cur.email && user.email) patch.email = user.email
    if (!cur.phoneNumber && user.phoneNumber) patch.phoneNumber = user.phoneNumber
    if (!cur.licensePlate && user.licensePlate) patch.licensePlate = user.licensePlate
    if (!cur.country && user.country) patch.country = user.country
    if (Object.keys(patch).length) setBookingInfo(patch)
  }, [user, setBookingInfo])

  const [touched, setTouched] = useState(false)

  function set<K extends keyof BookingInfo>(key: K, value: BookingInfo[K]) {
    setBookingInfo({ [key]: value } as Partial<BookingInfo>)
  }

  const errors = {
    name: !info.name.trim(),
    lastname: !info.lastname.trim(),
    email: !EMAIL_RE.test(info.email),
    phoneNumber: !info.phoneNumber.trim(),
    licensePlate: !info.licensePlate.trim(),
    privacy01: info.privacy01 !== 'true',
  }
  const valid = !Object.values(errors).some(Boolean)

  function onContinue() {
    setTouched(true)
    if (!valid) return
    navigate(stepPath(routeLocale, 'payment'))
  }

  const err = (k: keyof typeof errors) => (touched && errors[k] ? t('common.required') : undefined)

  return (
    <StepShell
      stepKey="info"
      title={t('info.title')}
      subtitle={t('info.subtitle')}
      onBack={() => navigate(stepPath(routeLocale, 'rate'))}
      onPrimary={onContinue}
      primaryDisabled={touched && !valid}
    >
      <div className="gsm-card space-y-5 p-5 sm:p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('info.firstName')} value={info.name} onChange={(e) => set('name', e.target.value)} error={err('name')} autoComplete="given-name" />
          <Field label={t('info.lastName')} value={info.lastname} onChange={(e) => set('lastname', e.target.value)} error={err('lastname')} autoComplete="family-name" />
          <Field label={t('info.email')} type="email" value={info.email} onChange={(e) => set('email', e.target.value)} error={err('email')} autoComplete="email" />
          <Field label={t('info.phone')} value={info.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} error={err('phoneNumber')} autoComplete="tel" />
          <Field label={t('info.plate')} value={info.licensePlate} onChange={(e) => set('licensePlate', e.target.value.toUpperCase())} error={err('licensePlate')} className="sm:col-span-1" />
          <div>
            <label className="gsm-label">{t('info.country')}</label>
            <Select
              value={info.country}
              options={COUNTRY_OPTIONS}
              onChange={(v) => set('country', v)}
              searchable
              ariaLabel={t('info.country')}
              placeholder={t('info.country')}
            />
          </div>
        </div>

        <div className="border-t border-ink-100 pt-2">
          <Checkbox
            checked={info.privacy01 === 'true'}
            onChange={(v) => set('privacy01', v ? 'true' : '')}
            label={
              <span>
                {t('info.privacy')} <span className="text-brand-600">*</span>
              </span>
            }
          />
          {touched && errors.privacy01 && <p className="ml-8 text-xs font-medium text-brand-600">{t('common.required')}</p>}
          <Checkbox checked={info.privacy02 === 'true'} onChange={(v) => set('privacy02', v ? 'true' : '')} label={t('info.marketing')} />
          <Checkbox checked={info.privacy03 === 'true'} onChange={(v) => set('privacy03', v ? 'true' : '')} label={t('info.profiling')} />
        </div>
      </div>
    </StepShell>
  )
}
